// ── YIELD PER ACRE BY INSURANCE UNIT, BESIDE THE UNIT'S GUARANTEE — the Claims page ─────────────
//
// Kevin, 2026-11-03: "no where in soybean claims manager shows their yield per acres, lets add
// that." The Claims page states production by county; this regroups THE SAME per-field
// production (claimAccounting.rowProduction — the figures the county card adds up) by the
// insurance unit each field is reported on, and sets each unit's yield beside its guarantee per
// acre (unitGuaranteeFlag.groupCoverageUnits / unitGuaranteePerAcre — the units and guarantees
// the Field Mapper's unit check already reads). Nothing here is a new production figure.
//
//   yield        bushels with a production figure ÷ the planted acres those bushels stand for.
//                Never ÷ the unit's planted acres while some of them have no figure: that counts
//                the rest as ZERO (the Rogers / Red Dawn inversion recorded in claimAccounting).
//   share        those acres ÷ the unit's planted acres (the carrier's unit rows, prevent plant out).
//   status       compared against the guarantee only once FULL (MIN_MAPPED_SHARE, 90% — Kevin,
//                2026-11-03: "Fully harvested over 90% sure"); before that the yield is "so far".
//
// ⚠ SCALE TICKETS ARE NOT IN A UNIT. A load names no field, so whole-farm figures (tickets,
// stored, fed, seed, a typed county total) stay on the farm line and are never spread over units.
// ⚠ GRAIN ONLY. A silage-insured (026) unit is settled in tons; it is named and not given a
// bushel yield (7 CFR 457.113 §12(b); Kevin: "we never merge those").
"use strict";

const { rowProduction, allocateFarmLevel } = require("./claimAccounting.js");
const { groupCoverageUnits, unitGuaranteePerAcre, MIN_MAPPED_SHARE } = require("./unitGuaranteeFlag.js");

const n = (v) => { if (v == null || v === "") return null; const x = Number(v); return Number.isFinite(x) ? x : null; };
const up = (v) => String(v == null ? "" : v).trim().toUpperCase();
const uz = (v) => String(v == null ? "" : v).trim().replace(/^0+(?=\d)/, "");
const r1 = (v) => Math.round(v * 10) / 10;
const isSilageType = (t) => ["026", "GSS", "SILAGE", "SILAGE-026"].indexOf(up(t)) >= 0;
const SILAGE_STATES = new Set(["silage_chopped", "silage_none"]);

const STATUS = Object.freeze({
  BELOW: "below",                 // 90%+ harvested and the yield is under the unit's guarantee
  AT_OR_ABOVE: "at_or_above",     // 90%+ harvested and it is not
  SO_FAR: "so_far",               // some acres have a figure, under 90% of the unit
  NONE: "none",                   // no acre of the unit has a production figure yet
  SILAGE: "silage",               // insured as silage: tons, not compared here
  NO_GUARANTEE: "no_guarantee",   // a yield, but no guarantee the comparison can stand on
  TOO_FEW: "too_few",             // bushels, but on too few acres for a per-acre figure to mean anything
});
// A yield read off a sliver of ground is noise (DERMODY: 71 bu over 1.1 of 28.5 ac; NAAS 0.14 ac;
// CRAIG RICE 0.46 ac). Under 2 accounted acres, a unit or field shows its bushels and no bu/ac —
// unless that sliver is the whole of it (90%+ of its planted acres).
const MIN_YIELD_ACRES = 2;
const readable = (acres, planted) => acres >= MIN_YIELD_ACRES || (planted > 0 && acres / planted >= MIN_MAPPED_SHARE);

const fieldLabel = (r) => [r.fsn, r.tract, r.fieldId].map((x) => (x == null || x === "" ? "?" : x)).join("-");

/**
 * buildUnitYield({ crop, cropYear, fcRows, coverageUnits, policies, farmLevel, unplaced })
 *
 *   fcRows         buildFieldCoverage(...).rows — each carries policyId and unitKey ("3|0")
 *   coverageUnits  crop_coverage_units rows for THIS crop (UNIT_YIELD_NEEDS lists the columns)
 *   policies       [{ id, policy_number }] — the crop year's policies
 *   farmLevel      buildFieldCoverage(...).farmLevelAppraisals (a worksheet line over many fields)
 *   unplaced       buildFieldCoverage(...).unplacedAppraisals (grain lines that name no field)
 *
 * Returns { units, fieldsOffUnit, fieldsNoAcres, unplacedOffUnit, totals }.
 */
function buildUnitYield({ crop, cropYear, fcRows = [], coverageUnits = [], policies = [], farmLevel = [], unplaced = [] } = {}) {
  const CY = Number(cropYear);
  const polNo = new Map((policies || []).map((p) => [p.id, p.policy_number || null]));
  const groups = groupCoverageUnits({ cropYear: CY, policyIds: polNo, coverageUnits });
  const alloc = allocateFarmLevel(fcRows, farmLevel);

  const units = groups.map((g) => {
    const first = g.rows[0];
    const planted = g.rows.filter((r) => !r.is_prevent_plant && n(r.acres) > 0);
    const plantedAcres = r1(planted.reduce((s, r) => s + n(r.acres), 0));
    const counties = [...new Set(g.rows.map((r) => up(r.county)).filter(Boolean))];
    const unitNo = uz(first.unit_number);
    const sub = g.structure === "OU" ? uz(g.subUnit) : null;
    const silage = planted.length > 0 && planted.every((r) => isSilageType(r.type_code));
    const grainRows = planted.filter((r) => !isSilageType(r.type_code));
    const gr = silage ? { perAcre: null, why: "insured as silage, settled in tons" } : unitGuaranteePerAcre(grainRows);
    return {
      key: g.key, policyId: first.policy_id, policyNumber: polNo.get(first.policy_id) || null,
      unit: sub != null ? `${unitNo.padStart(4, "0")}-${sub.padStart(4, "0")}` : unitNo.padStart(4, "0"),
      unitNo, sub, structure: g.structure, county: counties.join(" / ") || null,
      plantedAcres, silage,
      guaranteePerAcre: gr.perAcre, guaranteeBasis: gr.basis || null, guaranteeWhy: gr.perAcre == null ? gr.why : null,
      bu: 0, acres: 0, fields: [], unplacedBu: 0, unplacedAcres: 0,
    };
  });

  // The unit a field is reported on: same policy, same unit number, and the same sub-unit for an
  // optional unit (an enterprise or basic unit is every sub-unit of its number).
  const unitOf = (policyId, unitKey) => {
    if (!unitKey) return null;
    const [u, s] = String(unitKey).split("|");
    const hit = units.filter((x) => x.policyId === policyId && x.unitNo === uz(u) && (x.sub == null || x.sub === uz(s)));
    return hit.length === 1 ? hit[0] : null;
  };

  const fieldsOffUnit = [], fieldsNoAcres = [];
  for (const r of fcRows || []) {
    if (SILAGE_STATES.has(r.state)) continue;   // tons; the silage line states these
    const p = rowProduction(r, alloc);
    const planted = Math.max(0, (r.insuredAcres || 0) - (r.ppAcres || 0));
    const f = { field: fieldLabel(r), cluId: r.cluId, county: r.county || null, plantedAcres: r1(planted),
      acres: r1(p.acres), bu: p.bu,
      yield: p.acres > 0.05 && p.bu > 0 && readable(p.acres, planted) ? r1(p.bu / p.acres) : null, source: p.source };
    if (planted <= 0.05 && !(p.bu > 0)) continue;   // all prevent plant: nothing grown, nothing to show
    // ⚠ BUSHELS WITH NO PLANTED ACRES BEHIND THEM ARE NEVER IN A YIELD, AND NEVER DROPPED. The
    // combine crossed a field the acreage report gives no planted acres of this crop (DERMODY FSN
    // 4305 field 2: 0 ac reported, 547 bu — inside a unit it read 562.7 bu/ac; SULLIVAN FSN 50:
    // 12 ac all prevent plant, 736 bu). Listed apart, so units plus these add back to the card.
    if (p.bu > 0 && !(p.acres > 0.05)) { fieldsNoAcres.push(f); continue; }
    // A field the acreage report puts on no unit is listed apart the same way.
    const u = unitOf(r.policyId, r.unitKey);
    if (!u) { fieldsOffUnit.push(f); continue; }
    u.fields.push(f);
    u.bu += p.bu; u.acres += p.acres;
  }

  // A grain worksheet line that names no field is production of its unit (claimAccounting); it
  // counts at that unit when its unit number names exactly one unit of the farm.
  const unplacedOffUnit = [];
  for (const a of unplaced || []) {
    const q = n(a.quantity) || 0;
    if (!(q > 0)) continue;
    const [u, s] = String(a.unitKey || "").split("|");
    const hit = a.unitKey ? units.filter((x) => !x.silage && x.unitNo === uz(u) && (x.sub == null || x.sub === uz(s))) : [];
    if (hit.length !== 1) { unplacedOffUnit.push({ unit: a.unitLabel || null, county: a.county || null, bu: q, acres: n(a.acres) || 0 }); continue; }
    hit[0].unplacedBu += q; hit[0].unplacedAcres += n(a.acres) || 0;
  }

  for (const u of units) {
    // Unit-level appraisal acres stand for ground with no field figure, never more than is left.
    const unplAc = Math.min(u.unplacedAcres, Math.max(0, u.plantedAcres - u.acres));
    const bu = Math.round(u.bu + u.unplacedBu);
    const acres = r1(u.acres + unplAc);
    u.bu = bu; u.acres = acres; u.unplacedAcres = r1(unplAc); u.unplacedBu = Math.round(u.unplacedBu);
    u.share = u.plantedAcres > 0 ? Math.min(1, acres / u.plantedAcres) : null;
    u.full = u.share != null && u.share >= MIN_MAPPED_SHARE;
    const some = acres > 0.05 && bu > 0;
    u.yield = some && readable(acres, u.plantedAcres) ? r1(bu / acres) : null;
    u.vsGuarantee = u.yield != null && u.guaranteePerAcre != null ? r1(u.yield - u.guaranteePerAcre) : null;
    u.status = u.silage ? STATUS.SILAGE
      : some && u.yield == null ? STATUS.TOO_FEW
      : u.yield == null ? STATUS.NONE
      : !u.full ? STATUS.SO_FAR
      : u.guaranteePerAcre == null ? STATUS.NO_GUARANTEE
      : u.yield < u.guaranteePerAcre ? STATUS.BELOW : STATUS.AT_OR_ABOVE;
    u.fields.sort((a, b) => a.field.localeCompare(b.field, undefined, { numeric: true }));
  }
  // A unit with nothing planted (all prevent plant, or rows with no acres) has no yield to show.
  const shown = units.filter((u) => u.plantedAcres > 0.05 || u.bu > 0);
  shown.sort((a, b) => (a.county || "").localeCompare(b.county || "") || a.unit.localeCompare(b.unit)
    || String(a.policyNumber || "").localeCompare(String(b.policyNumber || "")));

  const grain = shown.filter((u) => !u.silage);
  const totals = {
    bu: grain.reduce((s, u) => s + u.bu, 0),
    acres: r1(grain.reduce((s, u) => s + u.acres, 0)),
    plantedAcres: r1(grain.reduce((s, u) => s + u.plantedAcres, 0)),
  };
  totals.yield = totals.acres > 0.05 && totals.bu > 0 && readable(totals.acres, totals.plantedAcres) ? r1(totals.bu / totals.acres) : null;
  return { crop: up(crop), cropYear: CY, units: shown, fieldsOffUnit, fieldsNoAcres, unplacedOffUnit, totals, minShare: MIN_MAPPED_SHARE };
}

// The columns each read must carry. Omit `unit_number` or `policy_id` and no field reaches a unit.
const UNIT_YIELD_NEEDS = Object.freeze({
  crop_coverage_units: ["policy_id", "crop_year", "county", "crop", "plan", "type_code", "unit_number", "sub_unit",
    "unit_structure", "is_prevent_plant", "acres", "aph_yield", "coverage_level", "acre_guarantee_qty", "acreage_status"],
  policy_fields: ["policy_id", "clu_id", "unit_number", "sub_unit", "acres", "pp_acres"],
});

module.exports = { MIN_YIELD_ACRES, buildUnitYield, STATUS, UNIT_YIELD_NEEDS };
